import {
  Alert,
  Anchor,
  Button,
  CloseButton,
  FileInput,
  Group,
  Image,
  Modal,
  SegmentedControl,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useRouterState } from "@tanstack/react-router";
import { useEffect, useState, type ClipboardEvent } from "react";
import { useTranslation } from "react-i18next";

import {
  FEEDBACK_CATEGORIES,
  FEEDBACK_SCREENSHOT_TYPES,
  feedbackScreenshotProblem,
  type FeedbackCategory,
} from "@msc/shared";

import {
  useFeedbackConfig,
  useSendFeedback,
  useUploadFeedbackScreenshot,
} from "../hooks/useFeedback.js";
import { useAppVersion } from "../hooks/useAuth.js";
import { describeApiError } from "../lib/errorText.js";
import { feedbackRumpf, screenshotAusZwischenablage } from "../lib/feedback.js";

/**
 * Der Feedback-Knopf im Kopf — legt ein Issue im Forgejo-Repo des Projekts an.
 *
 * **Er erscheint nur, wenn der Server sich als konfiguriert meldet.** Ohne
 * hinterlegtes Forgejo-Token führte er auf einen Dialog, der beim Absenden
 * 503 antwortet — und eine fehlende Konfiguration sähe aus wie ein Fehler der
 * Person, die gerade etwas melden wollte.
 */
export function FeedbackButton() {
  const { t } = useTranslation();
  const config = useFeedbackConfig();
  const [opened, handlers] = useDisclosure(false);

  if (config.data?.configured !== true) return null;

  return (
    <>
      <Button
        variant="subtle"
        size="compact-sm"
        color="gray"
        onClick={handlers.open}
        leftSection={<MegafonIcon />}
        aria-label={t("feedback.button")}
      >
        <Text component="span" size="sm" visibleFrom="sm">
          {t("feedback.button")}
        </Text>
      </Button>
      {/* Erst beim Öffnen einhängen: Der Dialog hält den Entwurf im Zustand,
          und ein frisch gemounteter Dialog ist ein leerer Entwurf — das spart
          das Zurücksetzen von Hand nach jedem Absenden. */}
      {opened && <FeedbackModal onClose={handlers.close} />}
    </>
  );
}

/** Kleines Megafon — eigenes SVG, weil das Projekt keine Icon-Bibliothek führt. */
function MegafonIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 11v2a1 1 0 0 0 1 1h3l6 4V6L7 10H4a1 1 0 0 0-1 1Z" />
      <path d="M17 9a3 3 0 0 1 0 6" />
      <path d="M7 14v5a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-3" />
    </svg>
  );
}

function FeedbackModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const routerState = useRouterState();
  const appVersion = useAppVersion();
  const senden = useSendFeedback();
  const anhang = useUploadFeedbackScreenshot();

  const [category, setCategory] = useState<FeedbackCategory>("fehler");
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [bild, setBild] = useState<File | null>(null);
  const [vorschau, setVorschau] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  // Objekt-URL wieder freigeben — sonst hält der Tab das Bild bis zum Neuladen.
  useEffect(() => {
    if (!bild) {
      setVorschau(null);
      return;
    }
    const url = URL.createObjectURL(bild);
    setVorschau(url);
    return () => URL.revokeObjectURL(url);
  }, [bild]);

  function nimmBild(datei: File | null) {
    if (!datei) {
      setBild(null);
      return;
    }
    const problem = feedbackScreenshotProblem({ type: datei.type, size: datei.size });
    if (problem !== null) {
      setFehler(t(`feedback.screenshot.${problem}Error`));
      return;
    }
    setFehler(null);
    setBild(datei);
  }

  /**
   * Einfügen aus der Zwischenablage — der übliche Weg zu einem Screenshot:
   * Fenster abfotografieren, in den Dialog einfügen. Der Umweg über „speichern
   * und dann auswählen" ist genau der Schritt, an dem ein Screenshot
   * unterbleibt.
   *
   * Der Aufsetzpunkt ist der ganze Dialog, nicht das Textfeld: Wer das Bild
   * einfügt, hat vorher nicht unbedingt ins Textfeld geklickt.
   */
  function beimEinfuegen(ereignis: ClipboardEvent<HTMLDivElement>) {
    const ergebnis = screenshotAusZwischenablage(
      Array.from(ereignis.clipboardData?.files ?? []),
    );
    if (ergebnis === null) return; // gewöhnlicher Text — nichts zu tun.
    ereignis.preventDefault();
    if ("problem" in ergebnis) {
      setFehler(t(`feedback.screenshot.${ergebnis.problem}Error`));
      return;
    }
    setFehler(null);
    setBild(ergebnis.datei);
  }

  const laeuft = senden.isPending || anhang.isPending;
  const absendbar = title.trim().length >= 3 && text.trim().length > 0;

  async function absenden() {
    setFehler(null);
    let erzeugt;
    try {
      erzeugt = await senden.mutateAsync(
        feedbackRumpf({
          category,
          title,
          text,
          // `location.search` ist bei TanStack Router das GEPARSTE Objekt;
          // die rohe Zeichenkette (inkl. `?`) steht in `searchStr`. Genau die
          // gehört in die Meldung — ein serialisiertes Objekt wäre für den,
          // der den Fehler nachstellen will, nicht dieselbe Adresse.
          ort: { pathname: routerState.location.pathname, search: routerState.location.searchStr },
          appVersion,
        }),
      );
    } catch (err) {
      setFehler(describeApiError(err, t));
      return;
    }

    // Das Issue steht. Ein misslungener Anhang darf es nicht entwerten —
    // deshalb ein Hinweis statt eines Fehlers, und der Dialog schließt.
    //
    // **Der Grund wandert mit.** Ein blosses „konnte nicht angehängt werden"
    // liesse offen, ob es an der Datei lag (leer, falsches Format) oder am
    // Server — und wer den Grund nicht kennt, probiert dasselbe Bild noch
    // einmal.
    let bildFehler: string | null = null;
    if (bild) {
      try {
        await anhang.mutateAsync({ number: erzeugt.number, datei: bild });
      } catch (err) {
        bildFehler = describeApiError(err, t);
      }
    }

    notifications.show({
      color: bildFehler ? "yellow" : "green",
      autoClose: 10_000,
      title: t("feedback.sentTitle"),
      message: (
        <Text size="sm">
          {bildFehler ? t("feedback.sentWithoutScreenshot") : t("feedback.sent")}{" "}
          <Anchor href={erzeugt.url} target="_blank" rel="noreferrer">
            {t("feedback.issueLink", { number: erzeugt.number })}
          </Anchor>
          {bildFehler ? ` — ${bildFehler}` : null}
        </Text>
      ),
    });
    onClose();
  }

  return (
    <Modal
      opened
      onClose={onClose}
      title={t("feedback.title")}
      size="lg"
      centered
      onPaste={beimEinfuegen}
    >
      <Stack gap="md">
        <Text size="xs" c="dimmed">
          {t("feedback.intro")}
        </Text>

        <SegmentedControl
          value={category}
          onChange={(wert) => setCategory(wert as FeedbackCategory)}
          data={FEEDBACK_CATEGORIES.map((wert) => ({
            value: wert,
            label: t(`feedback.category.${wert}`),
          }))}
          fullWidth
        />

        <TextInput
          label={t("feedback.titleLabel")}
          placeholder={t("feedback.titlePlaceholder")}
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
          maxLength={120}
          autoFocus
          required
        />

        <Textarea
          label={t("feedback.textLabel")}
          placeholder={t("feedback.textPlaceholder")}
          value={text}
          onChange={(e) => setText(e.currentTarget.value)}
          maxLength={5000}
          autosize
          minRows={4}
          maxRows={14}
          required
        />

        <FileInput
          label={t("feedback.screenshot.label")}
          description={t("feedback.screenshot.hint")}
          placeholder={t("feedback.screenshot.placeholder")}
          accept={FEEDBACK_SCREENSHOT_TYPES.join(",")}
          value={bild}
          onChange={nimmBild}
          clearable
        />

        {vorschau && (
          <Group align="flex-start" gap="xs">
            <Image src={vorschau} alt={t("feedback.screenshot.previewAlt")} h={120} w="auto" fit="contain" radius="sm" />
            <CloseButton
              aria-label={t("feedback.screenshot.remove")}
              onClick={() => setBild(null)}
            />
          </Group>
        )}

        {fehler && (
          <Alert color="red" title={t("common.error")}>
            {fehler}
          </Alert>
        )}

        <Text size="xs" c="dimmed">
          {t("feedback.privacy")}
        </Text>

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={laeuft}>
            {t("common.cancel")}
          </Button>
          <Button loading={laeuft} disabled={!absendbar} onClick={() => void absenden()}>
            {t("feedback.submit")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
